// docs/92 HM5: the commissioned huge bodies. The sim names them by appearance numbers past the
// Oryx creature frames (fk-sim `ART_RED_DRAGON`, `ART_MOUNTAIN_TROLL`); the client draws each from
// its own sprite (tools/huge-art.py → public/assets/huge/), one art pixel to one world pixel as the
// 24 px Oryx frames are, so a body is never scaled by its size: the art is already that big.

import { KIT_H, KIT_ORIGIN } from './heroKit';

/** Which kind of great creature a body is: each has its own aura, ground, moments and voice. */
export type HugeKind = 'dragon' | 'troll' | 'yeti' | 'worm' | 'spider' | 'serpent' | 'wyrm' | 'eagle' | 'ent' | 'giant' | 'kraken' | 'maw';

export interface HugeArt {
  /** The Phaser texture key (loaded in Boot). */
  key: string;
  url: string;
  kind: HugeKind;
  /** The sprite's size in art pixels. */
  w: number;
  h: number;
  /** The row its feet stand on (the image's origin sits there, at the footprint's feet point). */
  feet: number;
  /** What the HUD calls it. */
  name: string;
  /**
   * docs/95: which way the image faces as drawn (left when unset). Every effect point on the art
   * (`HugeAura`'s heads, mouths and eyes) is given in the image as drawn, and the board turns the
   * body by this to face its foe (`artFacing`, `faceOf`).
   */
  faces?: 'left' | 'right';
}

export const HUGE_ART: Readonly<Record<number, HugeArt>> = {
  901: { key: 'huge-red-dragon', url: '/assets/huge/red-dragon.png', kind: 'dragon', w: 96, h: 96, feet: 90, name: 'Red dragon' },
  902: { key: 'huge-mountain-troll', url: '/assets/huge/mountain-troll.png', kind: 'troll', w: 64, h: 64, feet: 63, name: 'Mountain troll' },
  // docs/94: fk-sim `ART_YETI` .. `ART_BLACK_DRAGON`
  903: { key: 'huge-yeti', url: '/assets/huge/yeti.png', kind: 'yeti', w: 64, h: 64, feet: 62, name: 'Yeti' },
  904: { key: 'huge-sandworm', url: '/assets/huge/sandworm.png', kind: 'worm', w: 96, h: 96, feet: 92, name: 'Sandworm' },
  905: { key: 'huge-spider-queen', url: '/assets/huge/spider-queen.png', kind: 'spider', w: 64, h: 64, feet: 62, name: 'Spider Queen' },
  906: { key: 'huge-basilisk', url: '/assets/huge/basilisk.png', kind: 'serpent', w: 64, h: 64, feet: 63, name: 'Basilisk' },
  907: { key: 'huge-black-dragon', url: '/assets/huge/black-dragon.png', kind: 'wyrm', w: 96, h: 96, feet: 94, name: 'Black dragon' },
  // docs/95: fk-sim `ART_GREAT_EAGLE` .. `ART_GRIM_GIANT`
  908: { key: 'huge-great-eagle', url: '/assets/huge/eagle.png', kind: 'eagle', w: 64, h: 64, feet: 60, name: 'Great eagle', faces: 'left' },
  909: { key: 'huge-ent', url: '/assets/huge/ent.png', kind: 'ent', w: 64, h: 64, feet: 62, name: 'Ent', faces: 'left' },
  910: { key: 'huge-grim-giant', url: '/assets/huge/grim-giant.png', kind: 'giant', w: 64, h: 64, feet: 63, name: 'Grim giant', faces: 'left' },
  // docs/178: fk-sim `ART_KRAKEN`, `ART_MAW` (the deep's two, both drawn 96 px for their 3×3 bodies)
  911: { key: 'huge-kraken', url: '/assets/huge/kraken.png', kind: 'kraken', w: 96, h: 96, feet: 93, name: 'Kraken', faces: 'left' },
  912: { key: 'huge-maw-of-the-deep', url: '/assets/huge/maw-of-the-deep.png', kind: 'maw', w: 96, h: 96, feet: 94, name: 'Maw of the Deep', faces: 'left' },
};

/** The commissioned body for an appearance, if it has one. */
export function hugeArt(appearance: number | undefined): HugeArt | undefined {
  return appearance === undefined ? undefined : HUGE_ART[appearance];
}

/** The commissioned body drawn under a texture key, if it is one. */
export function hugeArtByKey(key: string): HugeArt | undefined {
  return Object.values(HUGE_ART).find((a) => a.key === key);
}

/** How many times a body's texture is scaled on the board: an Oryx frame `size` times over (docs/79), a commissioned body once. */
export function bodyScale(key: string, size: number): number {
  return hugeArtByKey(key) ? 1 : Math.max(1, size);
}

/** Art px a hero's kit figure stands above its board cell's floor (docs/145): the kit art is a little tall. */
export const KIT_LIFT = 6;

/** Art px a body's figure stands above its board cell's floor: a kit hero's `KIT_LIFT`, every other body 0. */
export function bodyStand(key: string): number {
  return key.startsWith('hk:') ? KIT_LIFT : 0;
}

/** Art px a kit hero's ground marks (base ring, shadow) sit above the cell's floor: just under its feet. Its HP plate
 *  stays on the floor. */
export const KIT_GROUND_LIFT = 5;

/** Art px a body's ground marks stand above its board cell's floor (docs/145). */
export function groundStand(key: string): number {
  return key.startsWith('hk:') ? KIT_GROUND_LIFT : 0;
}

/** The image origin's y for a body texture: its feet row for commissioned art, the bottom edge for an Oryx frame. */
export function bodyOriginY(key: string): number {
  // docs/145: a hero's kit figure is drawn `KIT_LIFT` px up within its board cell, its feet off the cell's floor
  if (key.startsWith('hk:')) return (KIT_ORIGIN.y * KIT_H + bodyStand(key)) / KIT_H;
  const a = hugeArtByKey(key);
  return a ? a.feet / a.h : 1;
}

/**
 * docs/95: which way a body's image faces as drawn, 1 east or -1 west. The board's figures (the Oryx
 * frames) count as facing east unflipped (`UnitSprite.setFacing`, `SpellFx`, `StatusAura`); a
 * commissioned body faces as its art says.
 */
export function artFacing(key: string): 1 | -1 {
  const a = hugeArtByKey(key);
  return a && (a.faces ?? 'left') === 'left' ? -1 : 1;
}

/** Whether a body's image is flipped to face `dir` (1 east, -1 west) on the board. */
export function flipToFace(key: string, dir: 1 | -1): boolean {
  return artFacing(key) !== dir;
}

/** Which way a figure faces on the board, 1 east or -1 west: its art's way, turned when it is flipped. */
export function faceOf(img: { flipX: boolean; texture: { key: string } }): 1 | -1 {
  const f = artFacing(img.texture.key);
  return img.flipX ? (-f as 1 | -1) : f;
}
